import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useAuthDialog } from "@/components/AuthDialog";
import { LANGS } from "@/lib/translations";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Waves, Globe, LogOut, BellRing, UserCircle2, Menu, X, Home, Cloud, Users, BookOpen, ShieldCheck, Car, MapPin, Newspaper } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import PushBell from "@/components/PushBell";
import ProfileDialog, { avatarUrl } from "@/components/ProfileDialog";

// Navigation links rendered both inline (desktop) and inside the hamburger drawer (mobile).
const NAV_LINKS = [
  { href: "/", key: "nav_home", icon: Home },
  { href: "/#weather", key: "nav_weather", icon: Cloud },
  { href: "/#surf", key: "nav_surf", icon: Waves },
  { href: "/beaches", key: "nav_beaches", icon: MapPin },
  { href: "/#community", key: "nav_community", icon: Users },
  { href: "/blog", key: "nav_blog", icon: Newspaper },
  { href: "/history", key: "nav_history", icon: BookOpen },
  { href: "/sjds-locals", key: "nav_locals", icon: MapPin },
  { href: "/nicaragua-taxi", key: "nav_taxi", icon: Car },
];

// Scroll to a `#section` id, waiting a few frames for the target to mount
// (Home page uses lazy chunks — the section may not be in the DOM yet on the
// first navigation into "/").
function scrollToAnchor(id, attempt = 0) {
  const el = document.getElementById(id);
  if (el) {
    // NOTE: `scrollIntoView` correctly walks up to the real scroll container
    // (#root here), so it works even though #root is the scroller.
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  if (attempt < 20) {
    setTimeout(() => scrollToAnchor(id, attempt + 1), 100);
  }
}

// Hard scroll-to-top used by cross-page nav clicks. IMPORTANT: the app's real
// y-scroller is `#root` (see index.css), so `window.scrollTo(0,0)` alone is
// a no-op. We reset every possible scroller (#root, window, html, body) and
// re-fire twice via rAF to defeat lazy-chunk + image-load layout shifts.
function scrollToPageTopHard() {
  if (typeof window === "undefined") return;
  const html = document.documentElement;
  const prev = html.style.scrollBehavior;
  html.style.scrollBehavior = "auto";
  const jump = () => {
    try { window.scrollTo(0, 0); } catch (_e) { /* ignore */ }
    try { html.scrollTop = 0; document.body.scrollTop = 0; } catch (_e) { /* ignore */ }
    const root = document.getElementById("root");
    if (root) root.scrollTop = 0;
  };
  jump();
  requestAnimationFrame(() => {
    jump();
    requestAnimationFrame(() => {
      jump();
      html.style.scrollBehavior = prev;
    });
  });
}

function MobileDrawer({ open, onClose, alertCount, onOpenProfile, onLogout, onLogin, user, t, navigate, pathname }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  if (!open) return null;
  const isAdmin = user?.role === "admin";
  // Smooth navigation for every drawer link. Always uses react-router (no full
  // page reload). For `/#section` links we navigate to `/` first if needed,
  // then scroll to the anchor once the target section mounts.
  const closeAndGo = (href) => (e) => {
    e.preventDefault();
    onClose();
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (pathname !== "/") {
        navigate(`/#${id}`);
        // Wait for Home + its lazy chunks to render before scrolling.
        setTimeout(() => scrollToAnchor(id), 350);
      } else {
        scrollToAnchor(id);
      }
    } else if (href === "/") {
      if (pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        scrollToPageTopHard();
      }
    } else {
      navigate(href);
      scrollToPageTopHard();
    }
  };
  return (
    <div
      data-testid="mobile-drawer"
      className="fixed inset-0 z-50 md:hidden"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[color:var(--ink)]/45 backdrop-blur-sm" />
      <aside
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 bottom-0 w-[82%] max-w-[340px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right"
        style={{
          // Use the dynamic viewport unit (1dvh) so iOS Safari's address bar
          // doesn't cut off the bottom of the drawer. Falls back to 100vh.
          height: "100dvh",
          maxHeight: "100dvh",
          // Reserve room for iPhone home indicator / Android nav bar.
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b border-[color:var(--ink-2)]/10">
          <span className="font-bold tracking-tight text-base" style={{ fontFamily: "Outfit" }}>{t("menu")}</span>
          <button
            data-testid="drawer-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[color:var(--sand-2)]"
            aria-label={t("close")}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_LINKS.map((n) => {
            const Icon = n.icon;
            return (
              <a
                key={n.key}
                href={n.href}
                onClick={closeAndGo(n.href)}
                data-testid={`drawer-${n.key}`}
                className="flex items-center gap-3 px-5 py-3.5 text-[15px] font-medium text-[color:var(--ink)] hover:bg-[color:var(--sand-2)]"
              >
                <Icon className="w-5 h-5 text-[color:var(--ink-2)]" />
                {t(n.key)}
              </a>
            );
          })}
          {isAdmin && (
            <a
              href="/#admin"
              onClick={closeAndGo("/#admin")}
              data-testid="drawer-nav_admin"
              className="flex items-center gap-3 px-5 py-3.5 text-[15px] font-semibold text-[color:var(--coral)] hover:bg-[color:var(--coral)]/5"
            >
              <ShieldCheck className="w-5 h-5" />
              {t("nav_admin")}
            </a>
          )}
          {alertCount > 0 && (
            <a
              href="#alerts"
              onClick={closeAndGo("/#alerts")}
              data-testid="drawer-alerts"
              className="flex items-center justify-between gap-3 px-5 py-3.5 text-[15px] font-medium text-[color:var(--emergency)] hover:bg-[color:var(--emergency)]/5"
            >
              <span className="flex items-center gap-3">
                <BellRing className="w-5 h-5" />
                {t("alerts") || "Alerts"}
              </span>
              <Badge className="bg-[color:var(--emergency)] text-white text-[10px] h-5 min-w-[20px] px-1 rounded-full">
                {alertCount}
              </Badge>
            </a>
          )}
        </nav>
        <div className="border-t border-[color:var(--ink-2)]/10 p-4 space-y-2 shrink-0 bg-white">
          {user && user.id ? (
            <>
              <Button
                variant="outline"
                onClick={() => { onClose(); onOpenProfile(); }}
                data-testid="drawer-open-profile"
                className="w-full pill justify-start"
              >
                <UserCircle2 className="w-4 h-4 mr-2" /> {t("nav_profile")}
              </Button>
              <Button
                variant="outline"
                onClick={() => { onClose(); onLogout(); }}
                data-testid="drawer-logout"
                className="w-full pill justify-start text-[color:var(--emergency)] border-[color:var(--emergency)]/30"
              >
                <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => { onClose(); onLogin(); }}
              data-testid="drawer-login"
              className="w-full pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]"
            >
              {t("login")}
            </Button>
          )}
        </div>
      </aside>
    </div>
  );
}

export default function Header({ alertCount = 0 }) {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const { open: openAuth } = useAuthDialog();
  const [profileOpen, setProfileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleNavClick = (href) => (e) => {
    e.preventDefault();
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (pathname !== "/") {
        // Include the hash so App.js ScrollToTop skips its scroll-to-0 reset,
        // and the URL stays shareable.
        navigate(`/#${id}`);
        setTimeout(() => scrollToAnchor(id), 350);
      } else {
        scrollToAnchor(id);
      }
    } else if (href === "/") {
      if (pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigate("/");
        scrollToPageTopHard();
      }
    } else {
      navigate(href);
      scrollToPageTopHard();
    }
  };

  return (
    <header className="sticky top-0 z-30 glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
        <a href="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <img
            src="/brand/logo.png"
            alt="SJDS.app"
            className="w-11 h-11 rounded-2xl object-cover shadow-sm group-hover:rotate-[-3deg] transition-transform"
            style={{ background: "#FDFBF7" }}
          />
          <span className="flex flex-col leading-tight">
            <span className="font-bold tracking-tight text-[15px]" style={{ fontFamily: "Outfit" }}>SJDS.app</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--ink-2)]">San Juan del Sur</span>
          </span>
        </a>
        <div className="hidden lg:flex items-center gap-1 ml-4 text-sm text-[color:var(--ink-2)]">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--teal)] animate-pulse" />
          <span>{t("live_nicaragua")}</span>
        </div>
        {/* Desktop inline nav */}
        <nav className="hidden md:flex items-center gap-1 ml-4">
          {NAV_LINKS.map((n) => (
            <a
              key={n.key}
              href={n.href}
              onClick={handleNavClick(n.href)}
              data-testid={`nav-${n.key.replace("nav_", "")}`}
              className="text-sm font-medium px-3 py-1.5 rounded-full text-[color:var(--ink)] hover:bg-[color:var(--sand-2)]"
            >
              {t(n.key)}
            </a>
          ))}
          {user?.role === "admin" && (
            <a
              href="/#admin"
              onClick={handleNavClick("/#admin")}
              data-testid="nav-admin"
              className="ml-1 inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full text-[color:var(--coral)] bg-[color:var(--coral)]/8 hover:bg-[color:var(--coral)]/15"
            >
              <ShieldCheck className="w-4 h-4" />
              {t("nav_admin")}
            </a>
          )}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {alertCount > 0 && (
            <a
              href="#alerts"
              data-testid="alert-badge"
              className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-[color:var(--emergency)]/10 text-[color:var(--emergency)] hover:bg-[color:var(--emergency)]/15"
            >
              <BellRing className="w-4 h-4" />
              <Badge className="absolute -top-1 -right-1 bg-[color:var(--emergency)] text-white text-[10px] h-5 min-w-[20px] px-1 rounded-full">
                {alertCount}
              </Badge>
            </a>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="lang-toggle"
                variant="outline"
                size="sm"
                className="pill border-[color:var(--ink-2)]/20"
              >
                <Globe className="w-4 h-4 mr-1.5" />
                <span className="uppercase">{lang}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-2xl">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={lang === l.code ? "font-semibold" : ""}
                >
                  <span className="mr-2 text-base" aria-hidden>{l.flag}</span>
                  {l.label}
                  <span className="ml-auto text-[10px] uppercase tracking-widest text-[color:var(--ink-2)]">{l.code}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <PushBell />
          {/* Desktop user menu */}
          {user && user.id ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-btn" className="hidden md:flex items-center gap-1.5 pill border border-[color:var(--ink-2)]/20 px-2 py-1.5 hover:bg-[color:var(--sand-2)]">
                  {user.avatar_file_id ? (
                    <img src={avatarUrl(user.avatar_file_id)} alt="" className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <UserCircle2 className="w-6 h-6 text-[color:var(--ink-2)]" />
                  )}
                  <span className="text-sm font-medium">{user.name?.split(" ")[0] || user.username}</span>
                  {user.role === "admin" && <Badge variant="outline" className="text-[9px] uppercase tracking-widest border-[color:var(--coral)]/40 text-[color:var(--coral)]">Admin</Badge>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl">
                <DropdownMenuItem data-testid="open-profile" onClick={() => setProfileOpen(true)}>
                  <UserCircle2 className="w-4 h-4 mr-2" /> {t("nav_profile")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-[color:var(--emergency)]">
                  <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              data-testid="open-auth-btn"
              size="sm"
              onClick={() => openAuth("login")}
              className="hidden md:inline-flex pill bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-2)]"
            >
              {t("login")}
            </Button>
          )}
          {/* Mobile hamburger — visible only below md */}
          <button
            data-testid="hamburger-btn"
            onClick={() => setDrawerOpen(true)}
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--ink-2)]/20 hover:bg-[color:var(--sand-2)]"
            aria-label={t("menu")}
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        alertCount={alertCount}
        onOpenProfile={() => setProfileOpen(true)}
        onLogout={logout}
        onLogin={() => openAuth("login")}
        user={user}
        t={t}
        navigate={navigate}
        pathname={pathname}
      />
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </header>
  );
}
